import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SiteShell from "../components/SiteShell";
import { trackAction } from "../lib/api";
import StepEnergy from "./audit/StepEnergy";
import StepSite from "./audit/StepSite";
import StepResults from "./audit/StepResults";

const INITIAL = {
  tariff: 10,
  dgHours: 0,
  dgCostPerLitre: 95,
  location: "TN",
  pincode: "",
  loadProfile: "industrial",
  roofSqm: 0,
  peakLoadKw: 0,
  existingSolar: false,
};

/**
 * /audit — the Sovereign Audit mini-app.
 *
 * Three-step flow: energy situation → site → results. Results either lead
 * into an RFP form drawer (≥250 kWh) or a "co-own a share" reframe (<250 kWh).
 * All the math is client-side in ./audit/calc.js; the RFP submission is a
 * multipart POST to /api/rfp.
 */
export default function Audit() {
  const [step, setStep] = useState(1);
  const [values, setValues] = useState(INITIAL);

  // Analytics — start-of-audit event on mount.
  useEffect(() => {
    trackAction({ route: "sovereign-audit", source: "audit", audience: "customer" });
  }, []);

  const goTo = (nextStep) => {
    setStep(nextStep);
    trackAction({
      route: "sovereign-audit",
      source: "audit",
      sourceCell: `audit-step-${nextStep}`,
      sourceState: `step-${nextStep}`,
    });
    // Scroll to top of the audit body so each step reads from the top.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteShell>
      <section
        style={{
          maxWidth: 940,
          margin: "0 auto",
          padding:
            "clamp(48px, 7vw, 88px) clamp(20px, 5vw, 60px) clamp(60px, 8vw, 100px)",
        }}
        data-testid="audit-page"
      >
        <div className="gg-eyebrow" style={{ marginBottom: 10 }}>
          Sovereign Audit
        </div>
        <h1 className="gg-hero-title" style={{ marginBottom: 18, fontSize: "clamp(30px, 4.5vw, 48px)" }}>
          What could your site save?
        </h1>
        <p
          style={{
            color: "var(--text-dim)",
            fontSize: 16.5,
            lineHeight: 1.7,
            maxWidth: 640,
            marginBottom: 34,
          }}
        >
          Three friendly steps. Sensible defaults. Honest math. Ends in a
          real engineering conversation — not a brochure.
        </p>

        <Progress step={step} />

        <div style={{ marginTop: 34, minHeight: 320 }}>
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.28 }}
              >
                <StepEnergy
                  values={values}
                  onChange={setValues}
                  onNext={() => goTo(2)}
                />
              </motion.div>
            )}
            {step === 2 && (
              <motion.div
                key="s2"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.28 }}
              >
                <StepSite
                  values={values}
                  onChange={setValues}
                  onBack={() => goTo(1)}
                  onNext={() => goTo(3)}
                />
              </motion.div>
            )}
            {step === 3 && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.28 }}
              >
                <StepResults
                  values={values}
                  onBack={() => goTo(2)}
                  onEvent={(name, meta) =>
                    trackAction({
                      route: name === "rfp-submitted" ? "submit-rfp" : "sovereign-audit",
                      source: "audit",
                      sourceState: "step-3",
                      audience: meta?.variant || null,
                    })
                  }
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </SiteShell>
  );
}

function Progress({ step }) {
  return (
    <div
      data-testid="audit-progress"
      style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        maxWidth: 360,
      }}
    >
      {[1, 2, 3].map((n) => (
        <React.Fragment key={n}>
          <div
            data-testid={`audit-progress-${n}`}
            style={{
              width: 28,
              height: 28,
              borderRadius: 999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12.5,
              fontWeight: 600,
              background:
                step === n
                  ? "linear-gradient(135deg, var(--gold), var(--gold-deep))"
                  : step > n
                  ? "rgba(31,170,89,0.4)"
                  : "rgba(255,255,255,0.05)",
              color: step === n ? "var(--night)" : "var(--text-dim)",
              border: `1px solid ${
                step === n ? "transparent" : "var(--line)"
              }`,
            }}
          >
            {n}
          </div>
          {n < 3 && (
            <div
              style={{
                flex: 1,
                height: 1,
                background:
                  step > n
                    ? "rgba(31,170,89,0.4)"
                    : "var(--line)",
              }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
